import React from "react";
import { Lock, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

/*
  Contextual upgrade interstitial.
  Shown in place of any premium-only section when a free user lands on it.
  `feature`          — the specific feature they tried to access
  `featureBenefits`  — optional override; otherwise a default Premium benefit list is used.
*/
const DEFAULT_BENEFITS = [
  "Full access to 500+ SA clinical protocols (not just the top 50)",
  "Drug interaction checker, all 32 calculators, paediatric doses, antibiotic guide",
  "Unlimited ICD-10 code finder with assisted lookup",
];

export default function Paywall({ feature = "This feature", featureBenefits }) {
  const benefits = featureBenefits && featureBenefits.length ? featureBenefits : DEFAULT_BENEFITS;
  return (
    <div className="max-w-2xl mx-auto py-10" data-testid="paywall">
      <Card className="border-[#0B5FFF]/20 bg-white">
        <CardContent className="p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#EFF4FF] text-[#111827] flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Premium feature</div>
              <h2 className="font-heading text-2xl font-bold text-[#111827] mt-1" data-testid="paywall-feature-name">
                {feature} is part of Premium
              </h2>
              <p className="text-sm text-slate-600 mt-2">
                Your free account gives you permanent access to the 50 most common emergency protocols. Unlock the rest with a subscription — or start a free trial.
              </p>
            </div>
          </div>

          <ul className="mt-6 space-y-2.5" data-testid="paywall-benefits">
            {benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:items-center">
            <Link to="/pricing?trial=1" className="sm:flex-1">
              <Button className="bg-[#0B5FFF] hover:bg-[#0A54E0] h-11 w-full" data-testid="paywall-trial">
                <Sparkles className="w-4 h-4 mr-2" /> Start 3-day free trial
              </Button>
            </Link>
            <Link to="/pricing" className="text-sm text-[#111827] hover:underline font-medium inline-flex items-center" data-testid="paywall-view-plans">
              View all plans <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Link>
          </div>
          <p className="text-[11px] text-slate-500 mt-4">
            No card charged during your trial, and you're never charged automatically. Pay once for 30 days (R89) or a year (R899 — save 16%) and renew when it suits you.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
